<template>
  <div class="seal-rule-edit-page">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      title="录入电子印章规则"
      left-arrow
      @click-left="handleBack"
    />

    <div class="content-wrapper">
      <!-- 规则名称输入框 -->
      <div class="form-section">
        <van-field
          v-model="ruleName"
          label="规则名称"
          placeholder="请输入规则名称（备注）"
          maxlength="255"
        />
        <van-field
          v-model.number="standardPoint"
          label="标准通过分数"
          type="number"
          placeholder="请输入标准通过分数（0-100）"
          :max="100"
          :min="0"
        >
          <template #right-icon>
            <span style="color: #999; font-size: 12px;">分</span>
          </template>
        </van-field>
      </div>

      <!-- 印章区域 -->
      <div class="seal-section">
        <div class="seal-label">电子印章区域（用4个手指同时接触屏幕记录点位）</div>
        <div class="canvas-container">
          <canvas
            ref="canvas"
            width="400"
            height="400"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
          ></canvas>
          <p class="hint">用4个手指同时接触屏幕记录点位</p>
        </div>
        
        <!-- 点位信息显示 -->
        <div v-if="sealPoints.length > 0" class="points-info">
          <p><strong>已记录点位 ({{ sealPoints.length }}/4):</strong></p>
          <p v-for="(point, idx) in sealPoints" :key="idx">
            点{{ idx + 1 }}: [{{ point[0] }}, {{ point[1] }}]
          </p>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-section">
        <van-button
          type="warning"
          size="large"
          block
          @click="handleClear"
          :disabled="sealPoints.length === 0"
        >
          清除点位
        </van-button>
        <van-button
          type="primary"
          size="large"
          block
          @click="handleSave"
          :disabled="sealPoints.length !== 4 || !ruleName.trim() || standardPoint < 0 || standardPoint > 100"
          :loading="saving"
          style="margin-top: 12px;"
        >
          保存规则
        </van-button>
      </div>

      <!-- 提示信息 -->
      <div class="tip-section">
        <van-notice-bar
          :text="tipMessage"
          left-icon="info-o"
          color="#1989fa"
          background="#ecf9ff"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { saveSealRule } from "@/api/signApi";
import { Toast } from "vant";

export default {
  name: "SealRuleEdit",
  data() {
    return {
      ruleName: "", // 规则名称（备注）
      standardPoint: 90, // 标准通过分数（0-100），默认90
      sealPoints: [], // 点位数组 [[x, y], ...] 标准800x600像素坐标
      saving: false,
      canvas: null,
      ctx: null,
      // 标准坐标系（用于跨设备兼容）
      standardWidth: 800,
      standardHeight: 600
    };
  },
  computed: {
    tipMessage() {
      const count = this.sealPoints.length;
      if (count === 0) {
        return "请用4个手指同时接触屏幕记录点位";
      } else if (count < 4) {
        return `已记录 ${count} 个点位，还需要记录 ${4 - count} 个点位，请用4个手指同时接触屏幕`;
      } else {
        return "已记录4个点位，可以保存规则了";
      }
    }
  },
  mounted() {
    this.canvas = this.$refs.canvas;
    this.ctx = this.canvas.getContext('2d');
  },
  methods: {
    // 处理触摸开始事件
    handleTouchStart(e) {
      e.preventDefault();
      // 检查是否有4个手指同时接触
      if (e.touches.length === 4) {
        // 先清空画布和点位
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.sealPoints = [];
        
        const rect = this.canvas.getBoundingClientRect();
        // 计算缩放比例（canvas 实际尺寸 vs 显示尺寸）
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        // 收集4个手指的坐标并转换为标准800x600像素坐标
        for (let i = 0; i < 4; i++) {
          const touch = e.touches[i];
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          
          // 转换为 canvas 内部坐标（用于绘制）
          const canvasX = x * scaleX;
          const canvasY = y * scaleY;
          
          // 转换为标准800x600像素坐标（用于保存，保证跨设备兼容）
          const standardX = Math.round((x / rect.width) * this.standardWidth);
          const standardY = Math.round((y / rect.height) * this.standardHeight);
          
          this.sealPoints.push([standardX, standardY]);
          this.drawPoint(canvasX, canvasY, i + 1);
        }

        console.log('4个手指接触，点位记录（标准坐标）:', this.sealPoints);
        Toast.success("已记录4个点位");
      } else if (e.touches.length > 0 && e.touches.length < 4) {
        Toast(`请同时使用4个手指接触屏幕，当前检测到 ${e.touches.length} 个`);
      }
    },
    // 处理触摸结束事件
    handleTouchEnd(e) {
      // 手指离开时不做处理，保持已记录的点位
      if (e.touches.length === 0) {
        console.log('所有手指离开');
      }
    },
    // 在canvas上绘制点位
    drawPoint(x, y, number) {
      this.ctx.fillStyle = '#FF0000';
      this.ctx.beginPath();
      this.ctx.arc(x, y, 5, 0, 2 * Math.PI);
      this.ctx.fill();
      
      // 绘制点位编号
      this.ctx.fillStyle = '#000000';
      this.ctx.font = '12px Arial';
      this.ctx.fillText(number, x + 8, y - 8);
    },
    // 清除所有点位
    handleClear() {
      this.$dialog.confirm({
        title: "确认清除",
        message: "确定要清除所有点位吗？"
      })
        .then(() => {
          this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
          this.sealPoints = [];
          Toast("已清除所有点位");
        })
        .catch(() => {});
    },
    // 保存规则
    async handleSave() {
      if (!this.ruleName.trim()) {
        Toast("请输入规则名称");
        return;
      }

      if (this.sealPoints.length !== 4) {
        Toast("必须添加4个点位才能保存");
        return;
      }

      // 点位是标准800x600像素坐标，直接转换为JSON字符串
      const sealPointsStr = JSON.stringify(this.sealPoints);

      // 验证标准分数
      if (this.standardPoint === null || this.standardPoint === undefined || 
          this.standardPoint < 0 || this.standardPoint > 100) {
        Toast("标准通过分数必须在0-100之间");
        return;
      }

      this.saving = true;
      try {
        const data = {
          ruleType: "SEAL",
          point: Math.round(this.standardPoint), // 确保是整数
          sealPoints: sealPointsStr,
          remark: this.ruleName.trim()
        };

        await saveSealRule(data);
        Toast.success("保存成功！");
        
        // 延迟返回上一页
        setTimeout(() => {
          this.handleBack();
        }, 1500);
      } catch (error) {
        console.error("保存失败:", error);
        Toast.fail(error.message || "保存失败，请重试");
      } finally {
        this.saving = false;
      }
    },
    // 返回上一页
    handleBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped lang="less">
.seal-rule-edit-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.content-wrapper {
  padding: 12px;
}

.form-section {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.seal-section {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.seal-label {
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
  font-weight: 500;
}

.canvas-container {
  background: white;
  margin: 12px 0;
  border-radius: 4px;
  text-align: center;
  padding: 12px;
}

canvas {
  border: 1px solid #ddd;
  cursor: crosshair;
  max-width: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 8px;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 8px;
}

.points-info {
  background: white;
  padding: 12px;
  margin: 12px 0;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.points-info p {
  margin: 4px 0;
}

.action-section {
  margin-bottom: 12px;
}

.tip-section {
  margin-top: 12px;
}

// 响应式调整
@media (max-width: 375px) {
  canvas {
    width: 100%;
    height: 350px;
  }
}

@media (min-width: 768px) {
  canvas {
    width: 100%;
    max-width: 600px;
    height: 450px;
  }
}
</style>

